import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BlogArticle } from '../../models/content';
import { FileService } from '../../services/file.service';
import { ModalService } from '../../services/modal.service';
import { UserService } from '../../services/user.service';
import { contentSlug, CONTENT_SLUG_PATTERN } from '../../content-slug';

// Blog topics (picaluna-v3-integration-BLOG-CMS.md §2). Extend as needed.
const CATEGORIES = ['Grief', 'Planning', 'Costs', 'Ceremony', 'Cremation', 'Burial', 'News'];


/**
 * `YYYY-MM-DD` for the date the user actually picked, read from the LOCAL
 * calendar fields. Never use toISOString() here — it converts to UTC first,
 * which moves the date back a day everywhere east of Greenwich.
 */
function toLocalDateString(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Blog article editor — opened from ContentBlogListComponent. Returns the mutated
 * BlogArticle via dialogRef.close; the list persists via patchContent('blogs', …).
 *
 * Cloned from the charity editor, plus: a category dropdown, a publish-date
 * picker, an excerpt + a large Markdown body textarea, a native hero upload
 * (heroFileId, same as the charity logo), and a multi-select of endorsing
 * arrangers stored as a CSV (`endorsedBy`) so it round-trips as a scalar.
 */
@Component({
  selector: 'app-content-blog',
  templateUrl: './content-blog.component.html',
  styleUrls: ['./content-blog.component.scss'],
})
export class ContentBlogComponent implements OnInit {
  blog!: BlogArticle;
  isNew = false;
  /** True while the save request is in flight — Save spins, Cancel locks. */
  saving = false;
  form!: FormGroup;
  slugIsReadOnly = true;
  categories = CATEGORIES;
  heroFileId: number | null = null;
  heroPreview: string | null = null;
  arrangerOptions: { label: string; value: number }[] = [];
  endorsedBy: number[] = [];

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private fb: FormBuilder,
    private modalService: ModalService,
    private userService: UserService,
    private fileService: FileService
  ) {}

  ngOnInit(): void {
    this.blog = this.config.data.blog;
    this.isNew = !!this.config.data.isNew;
    this.heroFileId = typeof this.blog.heroFileId === 'number' ? this.blog.heroFileId : null;
    this.heroPreview = this.config.data.heroPreview ?? null;
    // An already-saved hero only carries its fileId — resolve a fresh signed URL
    // so the thumbnail shows on re-edit (and proves the S3 object is a valid image).
    if (this.heroFileId != null && !this.heroPreview) {
      this.fileService.getPreviewLink(String(this.heroFileId)).subscribe({
        next: (url) => (this.heroPreview = url),
        error: () => {},
      });
    }
    this.endorsedBy = (this.blog.endorsedBy || '')
      .split(',')
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isInteger(n) && n > 0);

    this.form = this.fb.group({
      title: new FormControl(this.blog.title ?? '', [Validators.required]),
      slug: new FormControl(this.blog.slug ?? '', [
        Validators.required,
        Validators.pattern(CONTENT_SLUG_PATTERN),
      ]),
      category: new FormControl(this.blog.category ?? CATEGORIES[0]),
      excerpt: new FormControl(this.blog.excerpt ?? ''),
      body: new FormControl(this.blog.body ?? ''),
      publishedAt: new FormControl(this.blog.publishedAt ? new Date(this.blog.publishedAt) : null),
      // New posts default to draft (published === true only for existing live posts).
      published: new FormControl(this.blog.published === true),
    });

    this.slugIsReadOnly = (this.blog.slug || '') === contentSlug(this.blog.title || '');

    // Load arrangers for the endorsements multi-select.
    this.userService.retrieveAll().subscribe({
      next: (users) => {
        this.arrangerOptions = (users || [])
          .map((u: any) => ({
            label: u?.name || `${u?.data?.firstName ?? ''} ${u?.data?.lastName ?? ''}`.trim(),
            value: u?.data?.id,
          }))
          .filter((o) => o.value != null && o.label)
          .sort((a, b) => a.label.localeCompare(b.label));
      },
      error: () => {
        this.arrangerOptions = [];
      },
    });
  }

  onTitleInput(): void {
    if (this.slugIsReadOnly) {
      this.form.get('slug')!.setValue(contentSlug(this.form.get('title')!.value || ''));
    }
  }

  toggleSlugEdit(): void {
    this.slugIsReadOnly = !this.slugIsReadOnly;
    if (this.slugIsReadOnly) {
      this.form.get('slug')!.setValue(contentSlug(this.form.get('title')!.value || ''));
    }
  }

  uploadHero(): void {
    const ref = this.modalService.fileUpload({
      title: 'Upload blog hero image',
      bucketPrefix: 'blog-heroes',
      // Raster only — the site's asset globs are {jpg,jpeg,png,webp,avif}, so a
      // GIF or SVG uploads and syncs but silently renders nothing.
      accept: 'image/jpeg,image/png,image/webp,image/avif',
      multiple: false,
    });
    ref.onClose.subscribe((result?: { files: { id: number; link?: string }[] }) => {
      const file = result?.files?.[0];
      if (!file) return;
      this.heroFileId = file.id;
      if (file.link) this.heroPreview = file.link;
    });
  }

  removeHero(): void {
    this.heroFileId = null;
    this.heroPreview = null;
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const value: any = { ...this.form.value };
    value.slug = contentSlug(value.slug || '');
    value.excerpt = (value.excerpt || '').trim() || null;
    value.body = (value.body || '').trim() || null;
    value.category = value.category || null;
    // p-calendar yields a Date → store an ISO string.
    // Store a DATE-ONLY string built from the local calendar day.
    //
    // p-calendar with no showTime holds LOCAL midnight, so toISOString() shifted
    // every Australian author's date back a day (10 Aug 00:00 AEST →
    // "2026-08-09T14:00:00.000Z"). Nothing downstream normalised it: the server
    // passes the string through, the sync spreads it verbatim, v3's schema is a
    // bare z.string(), and both v3 formatters use Intl with no timeZone — so the
    // article published under yesterday's date.
    //
    // Date-only also matches the existing corpus, which is already YYYY-MM-DD.
    value.publishedAt = value.publishedAt instanceof Date
      ? toLocalDateString(value.publishedAt)
      : value.publishedAt || null;
    value.heroFileId = this.heroFileId;
    value.endorsedBy = this.endorsedBy.length ? this.endorsedBy.join(',') : null;
    Object.assign(this.blog, value);
    // Save-then-close: the dialog stays open (Save spinning) until the server
    // confirms, so a publish cannot race an in-flight write; on rejection it
    // stays open with everything typed rather than losing the edit.
    const save = this.config.data.save as ((v: any) => any) | undefined;
    if (!save) {
      this.ref.close({ saved: true, blog: this.blog });
      return;
    }
    this.saving = true;
    save(this.blog).subscribe({
      next: () => this.ref.close({ saved: true, blog: this.blog, persisted: true }),
      error: (err: any) => {
        this.saving = false;
        this.modalService.error(err?.error?.error || err);
      },
    });
  }

  onCancel(): void {
    this.ref.close({ saved: false });
  }
}
